import { useSelector } from "react-redux"
import { useAppDispatch } from "../../reduxStore/hooks"
import { selectContent, ContentManagementState } from "./model"
import { DataStatus } from "../enums"
import { Content } from "./schema"
import { useEffect } from "react"

/**
 * Smart CMS content selector that automatically triggers lazy loading.
 * This is a drop-in replacement for useSelector(selectContent) that adds
 * automatic lazy loading without changing component code.
 *
 * @returns Content object with automatic lazy loading
 *
 * @example
 * // Instead of:
 * // const cmsContent = useSelector(selectContent)
 *
 * // Use:
 * const cmsContent = useSmartCMSContent()
 *
 * // Then access content as normal - it will auto-load if needed:
 * const taskboardTitle = cmsContent.taskboard?.title
 */
export const useSmartCMSContent = (): Content => {
  const dispatch = useAppDispatch()
  const content = useSelector(selectContent)

  const isOverridden = useSelector(
    (state: { contentManagement: ContentManagementState }) =>
      state.contentManagement.isContentOverridden,
  )

  const overrideContent = useSelector(
    (state: { contentManagement: ContentManagementState }) =>
      state.contentManagement.overrideContent,
  )

  const loadedCollections = useSelector(
    (state: { contentManagement: ContentManagementState }) =>
      state.contentManagement.loadedCollections,
  )

  const collectionLoadingStatus = useSelector(
    (state: { contentManagement: ContentManagementState }) =>
      state.contentManagement.collectionLoadingStatus,
  )

  // If content is overridden (GraphQL testing mode), return the override content directly
  // When override is cleared, we fall through to return the regular content (which was updated during override)
  if (isOverridden && overrideContent) {
    return overrideContent
  }

  // Return regular content (this will contain the override data after it's been set, even after clearing the flag)
  // This ensures the component re-renders when content changes

  // Create a proxy that intercepts property access and triggers loading
  const smartContent = new Proxy(content, {
    get(target, property: string | symbol) {
      if (typeof property === "string") {
        // Filter out built-in properties that should not be treated as collection names
        const builtInProperties = [
          "length",
          "constructor",
          "prototype",
          "toString",
          "valueOf",
          "hasOwnProperty",
          "isPrototypeOf",
          "propertyIsEnumerable",
          "toLocaleString",
        ]

        if (builtInProperties.includes(property)) {
          return target[property as keyof Content]
        }

        const collectionName = property as keyof Content
        const collectionContent = target[collectionName]
        const isLoaded = loadedCollections[collectionName] ?? false
        const isLoading =
          collectionLoadingStatus[collectionName] === DataStatus.LOADING

        // If content is not loaded and not currently loading, trigger lazy load
        if (!collectionContent && !isLoaded && !isLoading) {
          // Use setTimeout to avoid dispatching during render
          setTimeout(() => {
            import("./lazyCollectionLoader").then(({ loadCMSCollections }) => {
              dispatch(loadCMSCollections([collectionName]))
            })
          }, 0)
        }
      }

      return target[property as keyof Content]
    },
  })

  return smartContent as Content
}

/**
 * Hook to preload specific collections without accessing their content.
 * Useful when you know you'll need certain collections soon.
 *
 * @param collections Array of collection names to preload
 *
 * @example
 * // Preload collections that will be needed soon
 * usePreloadCollections(['taskboard', 'common'])
 */
export const usePreloadCollections = (collections: (keyof Content)[]): void => {
  const dispatch = useAppDispatch()

  const loadedCollections = useSelector(
    (state: { contentManagement: ContentManagementState }) =>
      state.contentManagement.loadedCollections,
  )

  const collectionLoadingStatus = useSelector(
    (state: { contentManagement: ContentManagementState }) =>
      state.contentManagement.collectionLoadingStatus,
  )

  useEffect(() => {
    const collectionsToLoad = collections.filter((collection) => {
      const isLoaded = loadedCollections[collection] ?? false
      const isLoading =
        collectionLoadingStatus[collection] === DataStatus.LOADING
      return !isLoaded && !isLoading
    })

    if (collectionsToLoad.length > 0) {
      import("./lazyCollectionLoader").then(({ loadCMSCollections }) => {
        dispatch(loadCMSCollections(collectionsToLoad))
      })
    }
  }, [collections, dispatch, loadedCollections, collectionLoadingStatus])
}
